/**
 * AppCard — shared presentational application card.
 *
 * Extracted from the inline Cloudscape `Cards` `cardDefinition` in
 * `Home/Apps.jsx` so the Application Marketplace and Quick Search render apps
 * identically and share favorite / rating / click-through logic.
 *
 * Two consumers, one source of card markup:
 *  - The Application Marketplace renders a Cloudscape `Cards` grid, so it keeps
 *    using a `{ header, sections }` definition produced by `appCardDefinition`.
 *  - Quick Search maps each App_Record directly to a self-contained `<AppCard>`.
 *
 * Both paths delegate the per-item markup (header menu, favorite control, star
 * rating, view count, body) to the same internal render helpers below, so the
 * two surfaces stay byte-for-byte equivalent.
 *
 * All pure helpers (`truncateDescription`, `statusToIndicator`,
 * `formatViewCount`, `resolveUrl`, `LABEL_COLOR_CLASSES`) come from
 * `appCardUtils.js`. Normalizations DD-1 (truncation boundary `> 200` + single
 * ellipsis `…`) and DD-2 (any unrecognized status → single "No status"
 * warning) live in that module and apply to both consumers.
 */

import React from "react";
import { Link } from "react-router-dom";
import { Container, Icon, SpaceBetween } from "@amzn/awsui-components-react";

import StarRating from "../Home/StarRating";
import { TEMPLATES } from "src/Ateam-BI/Templates/Constants";
import {
  truncateDescription,
  statusToIndicator,
  formatViewCount,
  resolveUrl as resolveUrlUtil,
  isExternalUrl,
  LABEL_COLOR_CLASSES,
} from "./appCardUtils";

/**
 * Favorite heart control. Mirrors the inline marketplace markup
 * (`icon_apps`/`icon_disabled`, heart-filled/heart, color `#ff9900`).
 *
 * Per Requirement 3.5 this control calls `stopPropagation`/`preventDefault`
 * so clicking the favorite never triggers the card's click-through navigation.
 */
const renderFavoriteControl = ({ item, isFavorite, favDisabled, onFavoriteToggle }) => {
  const onClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    // Guard against rapid clicks: while a toggle is in flight (`favDisabled`)
    // we drop further clicks so we never fire concurrent favorite toggles.
    if (!favDisabled && onFavoriteToggle) {
      onFavoriteToggle(item.app_id);
    }
  };
  return (
    <div className={favDisabled ? "icon_disabled" : "icon_apps"} disabled={favDisabled}>
      {isFavorite ? (
        <span style={{ cursor: "pointer", color: "#ff9900" }} onClick={onClick}>
          <Icon size="medium" name="heart-filled" />
        </span>
      ) : (
        <span style={{ cursor: "pointer", color: "#ff9900" }} onClick={onClick}>
          <Icon name="heart" size="medium" />
        </span>
      )}
    </div>
  );
};

/**
 * Star rating block. Mirrors the inline marketplace markup, including the
 * `averageRating`/`ratingCount` fallbacks driven by the user's own rating.
 *
 * Per Requirement 3.5 the wrapper calls `stopPropagation`/`preventDefault` so
 * clicking a star never triggers the card's click-through navigation.
 */
const renderStarRating = ({ item, userRating = 0, onRatingChange }) => (
  <div className="card-rating-bottom" onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}>
    <br></br>
    <br></br>
    <StarRating
      value={userRating || 0}
      averageRating={item.rating_avg || userRating || 0}
      ratingCount={item.rating_count || (userRating ? 1 : 0)}
      onChange={(rating) => { if (onRatingChange) onRatingChange(item.app_id, rating); }}
    />
  </div>
);

/**
 * View-count line. `formatViewCount` returns `"0"` for null/undefined, matching
 * the inline `view_count != null ? formatViewCount(view_count) : "0"` guard.
 */
const renderViewCount = (item) => (
  <div className="card-views-bottom">
    <span style={{ fontSize: "13px", display: "inline-flex", alignItems: "center", gap: "4px", whiteSpace: "nowrap" }}>
      <span>👁️</span>
      <span style={{ color: "#5f6b7a" }}>{formatViewCount(item.view_count)}</span>
    </span>
  </div>
);

/**
 * The card header menu: app-name (optionally a router `<Link>`), optional
 * AI-Powered badge, favorite control, star rating, and view count.
 *
 * @param {boolean} [nameAsLink=true] When true the name is a `<Link>` (the
 *   marketplace `Cards` path). When false the name is plain text because the
 *   standalone `AppCard` wraps the whole card in a single `<Link>`.
 */
const renderHeaderMenu = ({
  item,
  isFavorite,
  favDisabled,
  onFavoriteToggle,
  userRating,
  onRatingChange,
  resolveUrl,
  showAiBadge = true,
  nameAsLink = true,
  isFeatured = false,
}) => (
  <div className="header_menu">
    {isFeatured && (
      <div style={{ marginBottom: "4px" }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: "4px", fontSize: "12px", fontWeight: 500, color: "#5f6b7a", background: "#f2f3f3", borderRadius: "4px", padding: "2px 8px" }}>
          <Icon name="status-info" size="small" /> Featured
        </span>
      </div>
    )}
    <div className="link">
      {nameAsLink ? (
        isExternalUrl(resolveUrl(item)) ? (
          <a href={resolveUrl(item)}> {item.app_name}</a>
        ) : (
          <Link to={resolveUrl(item)}> {item.app_name}</Link>
        )
      ) : (
        <span className="app-card-name"> {item.app_name}</span>
      )}
      {showAiBadge && item.template_id === TEMPLATES.AI && (
        <span className="ai-powered-badge"><Icon name="gen-ai" /> AI-Powered</span>
      )}
    </div>
    {renderFavoriteControl({ item, isFavorite, favDisabled, onFavoriteToggle })}
    {renderStarRating({ item, userRating, onRatingChange })}
    {renderViewCount(item)}
  </div>
);

/**
 * The card body: truncated description (DD-1), the status indicator (DD-2),
 * then `use_case` label chips cycled through `LABEL_COLOR_CLASSES` by index.
 *
 * Every optional field is treated as optional: an absent `use_case` simply
 * yields no chips, and an absent/unrecognized `status` yields the single
 * "No status" warning, all without throwing.
 *
 * @param {Object}   item                 The App_Record to render.
 * @param {Function} [linkifyDescription] Optional `(text) => ReactNode` that
 *   transforms the (already-truncated) description into linkified content. Used
 *   by the Quick_Search_Surface to turn inline App_References into
 *   App_Hyperlinks (Req 1.1, 1.6, 1.9, 5.1, 5.3). When absent the description
 *   renders verbatim, so the Application Marketplace path is unchanged.
 */
const renderCardBody = (item, linkifyDescription) => {
  const description = truncateDescription(item?.app_description);
  const renderedDescription =
    typeof linkifyDescription === "function" ? linkifyDescription(description) : description;
  const indicator = statusToIndicator(item?.status);

  const list = [];
  list.push(
    <span key="status" style={{ color: indicator.color }}>
      <Icon name={indicator.type} variant={indicator.variant} />&nbsp;{indicator.label}
    </span>
  );
  if (Array.isArray(item?.use_case)) {
    item.use_case.forEach((uc, index) => {
      list.push(
        <span key={`uc-${index}`} className={LABEL_COLOR_CLASSES[index % LABEL_COLOR_CLASSES.length]}>{uc}</span>
      );
    });
  }

  return (
    <div className="card-body-content">
      <div>{renderedDescription}</div>
      <ul style={{ listStyleType: "none", paddingLeft: "0px", marginTop: "8px", flexWrap: "wrap" }}>
        <SpaceBetween direction="horizontal" size="m">
          {list.map((label, index) => (
            <li key={index}>{label}</li>
          ))}
        </SpaceBetween>
      </ul>
    </div>
  );
};

/**
 * The empty-height spacing div the inline marketplace card reserves below the
 * body (its `rating` section).
 */
const renderRatingSpacer = () => <div style={{ height: "48px" }}></div>;

/**
 * AppCard — renders a single App_Record as a self-contained card.
 *
 * The whole card is a single `<Link>` so a click anywhere navigates to the
 * resolved destination (Requirement 3.4); the favorite control and the star
 * rating opt out via `stopPropagation`/`preventDefault` (Requirement 3.5).
 *
 * @param {Object}   props
 * @param {Object}   props.item             The App_Record to render.
 * @param {boolean}  [props.isFavorite]     Current Favorite_State for `item.app_id`.
 * @param {boolean}  [props.favDisabled]    Disables the favorite control while a toggle is in flight.
 * @param {Function} [props.onFavoriteToggle] `(appId) => void` invoked on favorite click.
 * @param {number}   [props.userRating]     The current user's personal rating (0-5).
 * @param {Function} [props.onRatingChange] `(appId, rating) => void` invoked on star click (0 clears).
 * @param {Function} [props.resolveUrl]     `(item) => string` URL resolution; defaults to appCardUtils.resolveUrl.
 * @param {Function} [props.onNavigate]     Optional `(item) => void` side-effect hook fired on card click.
 * @param {boolean}  [props.showAiBadge=true] Render the AI-Powered badge when `template_id === TEMPLATES.AI`.
 * @param {Function} [props.linkifyDescription] Optional `(text) => ReactNode` that linkifies the card description (Quick_Search_Surface inline references). When absent the description renders verbatim.
 */
const AppCard = ({
  item,
  isFavorite = false,
  favDisabled = false,
  onFavoriteToggle,
  userRating = 0,
  onRatingChange,
  resolveUrl = resolveUrlUtil,
  onNavigate,
  showAiBadge = true,
  linkifyDescription,
}) => {
  if (!item) {
    return null;
  }

  const destination = resolveUrl(item);
  const cardInner = (
    <>
      {renderHeaderMenu({
        item,
        isFavorite,
        favDisabled,
        onFavoriteToggle,
        userRating,
        onRatingChange,
        resolveUrl,
        showAiBadge,
        nameAsLink: false,
      })}
      {renderCardBody(item, linkifyDescription)}
      {renderRatingSpacer()}
    </>
  );
  const linkStyle = { textDecoration: "none", color: "inherit", display: "block" };
  const onLinkClick = () => { if (onNavigate) onNavigate(item); };

  return (
    <Container className="app-card">
      {isExternalUrl(destination) ? (
        <a href={destination} className="app-card-link" onClick={onLinkClick} style={linkStyle}>
          {cardInner}
        </a>
      ) : (
        <Link to={destination} className="app-card-link" onClick={onLinkClick} style={linkStyle}>
          {cardInner}
        </Link>
      )}
    </Container>
  );
};

/**
 * appCardDefinition — Cloudscape `Cards` `{ header, sections }` factory.
 *
 * Lets the Application Marketplace keep passing a `cardDefinition` to its
 * `<Cards>` while delegating the per-item markup to the same internal render
 * helpers `AppCard` uses, so both surfaces render identically.
 *
 * @param {Object}   deps
 * @param {Object}   [deps.favoritesMap]      Map of `app_id` → truthy when favorited.
 * @param {boolean}  [deps.favDisabled]       Disables favorite controls during in-flight toggles.
 * @param {Function} [deps.favorite]          `(appId) => void` favorite toggle callback.
 * @param {Object}   [deps.ratingsMap]        Map of `app_id` → the user's personal rating.
 * @param {Function} [deps.handleRatingChange] `(appId, rating) => void` rating callback.
 * @param {Function} [deps.getUrl]            `(item) => string` URL resolution; defaults to appCardUtils.resolveUrl.
 * @param {Function} [deps.linkifyDescription] Optional `(text) => ReactNode` that linkifies the card description (Quick_Search_Surface inline references, Req 1.1, 1.6, 1.9, 5.1, 5.3). When absent the description renders verbatim (Marketplace path unchanged).
 * @param {Object}   [deps.history]           Router history (accepted for API parity; navigation uses `<Link>`).
 * @returns {{ header: Function, sections: Array }} A Cloudscape card definition.
 */
export const appCardDefinition = ({
  favoritesMap = {},
  favDisabled = false,
  favorite,
  ratingsMap = {},
  handleRatingChange,
  getUrl = resolveUrlUtil,
  linkifyDescription,
  featuredMap = {},
  history, // eslint-disable-line no-unused-vars
} = {}) => ({
  header: (item) =>
    renderHeaderMenu({
      item,
      isFavorite: !!favoritesMap[item.app_id],
      favDisabled,
      onFavoriteToggle: favorite,
      userRating: ratingsMap[item.app_id],
      onRatingChange: handleRatingChange,
      resolveUrl: getUrl,
      showAiBadge: true,
      nameAsLink: true,
      isFeatured: !!featuredMap[item.app_id],
    }),
  sections: [
    {
      id: "card_body",
      header: "",
      content: (item) => renderCardBody(item, linkifyDescription),
    },
    {
      id: "rating",
      content: () => renderRatingSpacer(),
    },
  ],
});

export default AppCard;
